import React from 'react'
import { useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import styles from './AuthForm.module.css'
import AppSecondaryBtn from '../ui/AppSecondaryBtn/AppSecondaryBtn.tsx'
import OtpInput from 'react-otp-input'
import { api } from '../../api.ts'

interface LoginUser {
  phone: string
  code: string
}

interface Props {
  navigate: (path: string) => void
}

interface AuthFormState {
  loginData: LoginUser
  errorMessage: string
  errorShown: boolean
  timer: number
  smsSent: boolean
  error: string | null
  codeError: string | null
  isSending: boolean
}

class AuthFormClassComponent extends React.Component<Props, AuthFormState> {
  timerInterval: ReturnType<typeof setTimeout> | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      loginData: {
        phone: '',
        code: ''
      },
      smsSent: false,
      errorShown: false,
      errorMessage: '',
      codeError: null,
      error: null,
      timer: Number(localStorage.getItem('timerEnd')) || 0,
      isSending: false
    }

    this.sendSMSCode = this.sendSMSCode.bind(this)
    this.startTimer = this.startTimer.bind(this)
  }

  componentDidMount() {
    localStorage.removeItem('timer') // to delete deprecated timer
    const timerEnd = Number(localStorage.getItem('timerEnd'))
    if (timerEnd) {
      const timeLeft = Math.max(timerEnd - Date.now(), 0)
      this.setState({ timer: Math.round(timeLeft / 1000) })
      if (timeLeft > 0) {
        this.startTimer()
      } else {
        localStorage.removeItem('timerEnd')
      }
    }
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  }

  startTimer() {
    const endTime = Number(localStorage.getItem('timerEnd'))
    this.timerInterval = setInterval(() => {
      const timeLeft = Math.max(endTime - Date.now(), 0)
      this.setState({ timer: Math.round(timeLeft / 1000) })
      if (timeLeft <= 0) {
        if (this.timerInterval) {
          clearInterval(this.timerInterval)
        }
        localStorage.removeItem('timerEnd')
      }
    }, 1000)
  }

  async sendSMSCode(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.stopPropagation()
    const me = this
    me.setState({ error: null, isSending: true }, async () => {
      await api
        .post('/participant/auth/sign_in_up', { cell: this.state.loginData.phone })
        .then((resp: any) => {
          localStorage.setItem('jwt', resp?.content)
          me.setState({ smsSent: true })

          const timerEnd = Date.now() + 60000
          this.setState({ timer: 59 }, () => {
            localStorage.setItem('timerEnd', String(timerEnd))
            me.startTimer()
          })
        })
        .catch((err) => {
          const errorMessage =
            err?.response?.data?._embedded?.errors[0]?.message || 'Произошла ошибка'
          me.setState({ errorShown: true, smsSent: false, errorMessage: errorMessage })
        })
        .finally(() => {
          me.setState({ isSending: false })
        })
    })
  }

  btnShown = (): boolean => {
    const regex = /^\d{6}$/
    return regex.test(this.state.loginData.code) && !!this.state.loginData.phone
  }

  submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { navigate } = this.props

    await api
      .post('/participant/auth/check_code', {
        cell: this.state.loginData.phone,
        code: this.state.loginData.code
      })
      .then((resp) => {
        localStorage.setItem('token', resp.data.content)
        api.defaults.headers.common['Authorization'] = `${resp.data.content}`

        this.setState({ errorShown: false })
        // navigate('/home')
        navigate('/welcome')
      })
      .catch((err: Error) => {
        console.log('login error:', err)
        this.setState({ codeError: 'Неправильный код' })
      })
  }

  backHandler = () => {
    this.setState({ errorShown: false })
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    this.setState((prevState) => ({
      loginData: { ...prevState.loginData, [name]: value }
    }))
  }

  handleOTP = (otp: string) => {
    this.setState((prevState) => ({
      loginData: { ...prevState.loginData, ['code']: otp }
    }))
  }

  render() {
    const me = this
    const { errorShown, loginData, timer, errorMessage } = this.state
    return (
      <>
        {!errorShown && <h2 className="my-0 mb-3 text-xl font-semibold text-white">Авторизация</h2>}
        {!errorShown && (
          <form className="space-y-6" onSubmit={this.submitHandler}>
            {/* Input fields and labels */}
            <div>
              <label htmlFor="phone" className="block text-lg font-medium text-white">
                Укажите номер телефона
              </label>
              <span className="block text-sm font-normal text-white">
                Вам будет отправлен код-пароль для входа
              </span>
              <div className="mt-3">
                <InputMask
                  id="phone"
                  name="phone"
                  type="tel"
                  mask="+79999999999"
                  placeholder="+7 (999) 999-99-99"
                  maskChar={null}
                  value={loginData.phone}
                  onChange={this.handleInputChange}
                  className="block w-full px-3 py-4 text-xl placeholder-gray-400 border border-white rounded-md shadow-sm appearance-none focus:outline-none focus:ring-white focus:border-white"
                  required
                />
                {me.state.error && <p style={{ color: 'red' }}>{me.state.error}</p>}
              </div>
            </div>

            <button
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                me.sendSMSCode(e)
              }}
              disabled={timer > 0 || this.state.isSending}
              className="bg-[#F3F3F3] w-full text-base rounded-xl py-5 px-4 focus:outline-none focus:ring-white focus:border-white font-semibold"
            >
              {timer > 0 ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: `Отправить код повторно <br />(через ${timer} сек.)`
                  }}
                />
              ) : (
                'Отправить код'
              )}
            </button>

            {me.state.smsSent && (
              <div>
                <label htmlFor="code" className="block text-lg font-medium text-white">
                  Введите код-пароль из СМС
                </label>
                <div className="mt-3">
                  <OtpInput
                    value={loginData.code}
                    onChange={this.handleOTP}
                    numInputs={6}
                    renderSeparator={<span></span>}
                    renderInput={(props) => <input {...props} required id="code" name="code" />}
                    containerStyle={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      columnGap: '8px'
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '56px',
                      padding: '0 8px',
                      fontSize: '18px',
                      borderRadius: '8px',
                      border: '1px solid #E5E5E5',
                      backgroundColor: '#FFF',
                      color: '#000',
                      fontWeight: '600',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      KhtmlUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      userSelect: 'none'
                    }}
                    inputType="number"
                  />
                </div>
                {me.state.codeError && <p style={{ color: 'red' }}>{me.state.codeError}</p>}
              </div>
            )}

            {/* Submit button */}
            {this.btnShown() && (
              <button
                type="submit"
                className="bg-[#F3F3F3] w-full text-base rounded-xl py-5 px-4 focus:outline-none focus:ring-white focus:border-white font-semibold"
              >
                Продолжить
              </button>
            )}
          </form>
        )}
        {/* Error message */}
        {errorShown && (
          <div className={`${styles.errorLoginWrapper}`}>
            <div className={`${styles.icon}`}>
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="text-xl font-semibold text-center">{errorMessage}</div>
            <div className="text-lg font-normal text-center">
              Пожалуйста, обратитесь в региональное отделение Партии для уточнения данных.
            </div>
            <AppSecondaryBtn className="!bg-white w-full mt-10" onClick={this.backHandler}>
              Назад
            </AppSecondaryBtn>
          </div>
        )}
      </>
    )
  }
}

const AuthForm = () => {
  const navigate = useNavigate()

  return <AuthFormClassComponent navigate={navigate} />
}

export default AuthForm
